import {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { flushSync } from 'react-dom';
import { ShopSwitchOverlay, type ShopSwitchVisualState } from '@/components/ShopSwitchOverlay';
import {
  isShopQuery,
  waitForShopQueries,
  SHOP_SWITCH_SLOW_MS,
  SHOP_SWITCH_TIMEOUT_MS,
} from '@/lib/shopSwitch';
import * as Sentry from '@sentry/react';
import { usePaywallStore } from '@/stores/paywallStore';
import { useAuth } from '@/contexts/AuthContext';

const SHOP_KEY = 'current_shop_id';

interface Shop {
  id: string;
  name: string;
  role: string;
}

interface ShopContextType {
  currentShop: Shop | null;
  shops: Shop[];
  selectShop: (shop: Shop, options?: { destination?: string }) => void;
  shopRevision: number;
  isSwitching: boolean;
}

const ShopContext = createContext<ShopContextType | null>(null);

export interface ShopNavigation {
  destination: () => string;
  navigate: (path: string) => Promise<void>;
  resolve: (path: string, shopId: string, signal: AbortSignal) => Promise<string>;
  prepare: (path: string) => Promise<boolean>;
  canLeave: () => boolean;
}

export function ShopProvider({
  children,
  navigation,
}: {
  children: ReactNode;
  navigation?: ShopNavigation;
}) {
  const { user, isLoading } = useAuth();
  const queryClient = useQueryClient();
  const shops = useMemo(() => user?.shops ?? [], [user?.shops]);
  const [currentShop, setCurrentShop] = useState<Shop | null>(null);

  const [transition, setTransition] = useState<ShopSwitchVisualState | null>(null);
  const [shopRevision, setShopRevision] = useState(0);
  const [covered, setCovered] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const attemptRef = useRef<AbortController | null>(null);
  const switching = useRef(false);
  const previous = useRef<Shop | null>(null);
  const target = useRef<Shop | null>(null);
  const destination = useRef('');
  const runRef = useRef<(shop: Shop) => void>(() => {});

  useEffect(
    () => () => {
      attemptRef.current?.abort();
    },
    [],
  );
  useEffect(() => {
    if (isLoading || user) return;
    attemptRef.current?.abort();
    switching.current = false;
    setTransition(null);
    setCovered(false);
  }, [isLoading, user]);

  // Restore from localStorage or auto-select if single shop
  useEffect(() => {
    // While auth is still resolving (e.g. right after a page refresh), `user`
    // is null and `shops` is empty. Do NOT touch localStorage here — clearing
    // current_shop_id at this point would wipe the previously selected shop
    // before the auth query returns, making the selection reset to the first
    // shop on every refresh.
    if (isLoading) return;

    if (shops.length === 0) {
      setCurrentShop(null);
      localStorage.removeItem(SHOP_KEY);
      return;
    }

    const savedId = localStorage.getItem(SHOP_KEY);
    const saved = savedId ? (shops.find((s) => s.id === savedId) ?? null) : null;

    if (saved) {
      setCurrentShop(saved);
      Sentry.setTag('shop_id', saved.id);
    } else {
      const first = shops[0];
      if (first) {
        setCurrentShop(first);
        localStorage.setItem(SHOP_KEY, first.id);
        Sentry.setTag('shop_id', first.id);
      }
    }
  }, [shops, isLoading]);

  const run = useCallback(
    async (shop: Shop) => {
      attemptRef.current?.abort();
      const attempt = new AbortController();
      attemptRef.current = attempt;
      const valid = () => !attempt.signal.aborted;
      let contextReady = false;
      const fail = () => {
        if (!valid()) return;
        setTransition({ name: shop.name, phase: 'error', slow: false });
        attempt.abort();
      };
      const slowTimer = window.setTimeout(() => {
        if (valid()) setTransition((state) => (state ? { ...state, slow: true } : state));
      }, SHOP_SWITCH_SLOW_MS);
      const timeout = window.setTimeout(() => {
        if (!valid()) return;
        if (contextReady) {
          // Slow optional widgets cannot trap an otherwise usable destination.
          // Their own loading/error states remain visible on the page.
          setTransition(null);
          attempt.abort();
        } else fail();
      }, SHOP_SWITCH_TIMEOUT_MS);
      try {
        usePaywallStore.getState().closePaywall();
        // Commit the cover and unmount all shop consumers BEFORE clearing caches.
        flushSync(() => {
          setCovered(true);
          setTransition({ name: shop.name, phase: 'preparing', slow: false });
        });
        await queryClient.cancelQueries({ predicate: isShopQuery });
        if (!valid()) return;
        queryClient.removeQueries({ predicate: isShopQuery });
        localStorage.setItem(SHOP_KEY, shop.id);
        setCurrentShop(shop);
        Sentry.setTag('shop_id', shop.id);
        if (navigation) {
          const path = await navigation.resolve(destination.current, shop.id, attempt.signal);
          if (!valid()) return;
          await navigation.navigate(path);
        }
        if (!valid()) return;
        flushSync(() => {
          setShopRevision((revision) => revision + 1);
          setTransition({ name: shop.name, phase: 'loading', slow: false });
        });
        contextReady = true;
        await waitForShopQueries(queryClient, attempt.signal);
        if (!valid()) return;
        setTransition(null);
      } catch {
        fail();
      } finally {
        window.clearTimeout(slowTimer);
        window.clearTimeout(timeout);
      }
    },
    [queryClient, navigation],
  );
  runRef.current = (shop) => {
    void run(shop);
  };

  const selectShop = useCallback(
    (shop: Shop, options?: { destination?: string }) => {
      const allowed = shops.find((candidate) => candidate.id === shop.id);
      if (switching.current || currentShop?.id === shop.id) return;
      // Onboarding selects an API-created shop before refreshing auth. Preserve
      // that synchronous initialization without remounting its success wizard.
      if (!allowed || !currentShop) {
        setCurrentShop(shop);
        localStorage.setItem(SHOP_KEY, shop.id);
        Sentry.setTag('shop_id', shop.id);
        void queryClient.invalidateQueries();
        return;
      }
      if (navigation && !navigation.canLeave()) return;
      previous.current = currentShop;
      target.current = allowed;
      destination.current = options?.destination ?? navigation?.destination() ?? '';
      switching.current = true;
      void (async () => {
        try {
          if (navigation && !(await navigation.prepare(destination.current))) {
            switching.current = false;
            return;
          }
          await run(allowed);
        } catch {
          switching.current = false;
        }
      })();
    },
    [currentShop, shops, navigation, run, queryClient],
  );

  const onExited = useCallback(() => {
    switching.current = false;
    setCovered(false);
  }, []);

  useLayoutEffect(() => {
    if (covered || shopRevision === 0) return;
    const frame = requestAnimationFrame(() => {
      const heading =
        contentRef.current?.querySelector<HTMLElement>('main h1') ??
        contentRef.current?.querySelector<HTMLElement>('main');
      if (heading) {
        const originalTabIndex = heading.getAttribute('tabindex');
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        heading.addEventListener(
          'blur',
          () => {
            if (originalTabIndex === null) heading.removeAttribute('tabindex');
            else heading.setAttribute('tabindex', originalTabIndex);
          },
          { once: true },
        );
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [covered, shopRevision]);

  return (
    <ShopContext.Provider
      value={{ currentShop, shops, selectShop, shopRevision, isSwitching: covered }}
    >
      <div
        ref={contentRef}
        className="contents"
        {...(covered ? { inert: '' } : {})}
        aria-hidden={covered || undefined}
      >
        {(!transition || transition.phase === 'loading') && (
          <div key={shopRevision} className="contents">
            {children}
          </div>
        )}
      </div>
      <ShopSwitchOverlay
        state={transition}
        onRetry={() => {
          if (target.current) runRef.current(target.current);
        }}
        onBack={() => {
          if (previous.current) {
            target.current = previous.current;
            runRef.current(previous.current);
          }
        }}
        onExited={onExited}
      />
    </ShopContext.Provider>
  );
}

export function useShop() {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
}
